import { redirect, RouteObject } from 'react-router-dom'

import { JourneyNotFound } from '@cash-web/shared-journey-feature-main'
import { JourneyGlobalLayout, JourneyLayoutMaxWidth } from '@cash-web/shared-journey-feature-main'

import { JourneyDefaultError } from '../error/components/JourneyDefaultError'

const webReloadRoute: RouteObject = {
  path: 'web/view/reload',
  loader: async () => {
    return window.location.reload()
  },
}

const notFoundRedirectRoute: RouteObject = {
  path: '*',
  loader: async ({ request }) => {
    const url = new URL(request.url)
    const clientRouteUrl = `https://internal.cash.app${url.pathname}${url.search}`
    const params = new URLSearchParams({
      clientRouteUrl,
    })

    throw redirect(`/not-found?${params.toString()}`)
  },
}

/**
 * Intelligently merges journey routes based on their paths.
 *
 * Routes with paths starting with 'dl/view/' or 'web/view/' get wrapped in global > [prefix]/* > max-width layouts.
 * Other root-level dl routes get wrapped in global layout only.
 *
 * Developers don't need to know the structure - just import routes and merge them.
 *
 * @example
 * ```tsx
 * import { mergeJourneyRoutes, activityItemRoute, flowGetRoute } from '@cash-web/shared-journey-feature-routes'
 *
 * const routes = mergeJourneyRoutes([activityItemRoute, flowGetRoute])
 * <JourneyProvider routes={routes}>
 * ```
 */
export function mergeJourneyRoutes(routes: RouteObject[]): RouteObject[] {
  // Add the web reload route to the routes array
  const allRoutes = routes.concat(webReloadRoute)

  // Categorize routes by their layout needs based on path
  const rootRoutes: RouteObject[] = []
  const dlViewRoutes: RouteObject[] = []
  const webViewRoutes: RouteObject[] = []
  const treehouseRoutes: RouteObject[] = []

  for (const route of allRoutes) {
    // Determine layout based on path prefix
    if (
      route.path?.startsWith('dl/flow/') ||
      route.path?.startsWith('dl/scenario/') ||
      route.path?.startsWith('web/flow/')
    ) {
      rootRoutes.push(route)
    } else if (route.path?.startsWith('dl/view/')) {
      // Strip the 'dl/view/' prefix since we'll nest it under dl/view/*
      const pathWithoutPrefix = route.path.replace(/^dl\/view\//, '')
      dlViewRoutes.push({ ...route, path: pathWithoutPrefix })
    } else if (route.path?.startsWith('web/view/')) {
      // Strip the 'web/view/' prefix since we'll nest it under web/view/*
      const pathWithoutPrefix = route.path.replace(/^web\/view\//, '')
      webViewRoutes.push({ ...route, path: pathWithoutPrefix })
    } else if (route.path?.startsWith('dl/')) {
      rootRoutes.push(route)
    } else if (route.path?.startsWith('treehouse/')) {
      // Strip the 'treehouse/' prefix since we'll nest it under treehouse/*
      const pathWithoutPrefix = route.path.replace(/^treehouse\//, '')
      treehouseRoutes.push({ ...route, path: pathWithoutPrefix })
    }
  }

  // Add redirect routes to catch unsupported routes
  dlViewRoutes.push(notFoundRedirectRoute)
  treehouseRoutes.push(notFoundRedirectRoute)

  // Build the route tree
  const children: RouteObject[] = [...rootRoutes]

  if (dlViewRoutes.length > 0) {
    children.push({
      path: 'dl/view/*',
      element: <JourneyLayoutMaxWidth />,
      children: dlViewRoutes,
    })
  }

  if (webViewRoutes.length > 0) {
    children.push({
      path: 'web/view/*',
      element: <JourneyLayoutMaxWidth />,
      children: webViewRoutes,
    })
  }

  if (treehouseRoutes.length > 0) {
    children.push({
      path: 'treehouse/*',
      children: treehouseRoutes,
    })
  }

  /**
   * dl/background routes perform a background request and/or clear cached data.
   * On web we manage our caching independently and handle these routes as a noop.
   *
   * Omitting this route will result in a redirect to mobile which we want to prevent
   * for this type of client route.
   */
  children.push({
    path: 'dl/background/*',
    loader: async () => {
      return true
    },
  })

  children.push(notFoundRedirectRoute)

  return [
    {
      path: '/*',
      element: <JourneyGlobalLayout fallbackComponent={JourneyDefaultError} />,
      errorElement: <JourneyDefaultError />,
      children,
    },
    {
      path: '/not-found',
      element: <JourneyNotFound />,
    },
  ]
}
